<script setup>
import { RouterLink } from 'vue-router';
import IconChevronRight from '../icons/IconChevronRight.vue';
import IconFile from '../icons/IconFile.vue';
import IconLock from '../icons/IconLock.vue';
import IconStar from '../icons/IconStar.vue';
import { metaLine } from '../../composables/usePortalLibrary';

/**
 * One file in the Library, and under "Also in your library" on a file's page
 * (prototype .row, build 30 Sep 12:40). The name is the link, stretched over
 * the row, so the star beside it can be a button of its own (XN-4682): tapping
 * it never opens the file. `saved` null draws no star (staff previewing). A
 * personal file gets the amber lock tile (prototype `ico wa`), a shared one
 * the purple file tile (`ico b`).
 */
const props = defineProps({
  item: { type: Object, required: true },
  saved: { type: Boolean, default: null },
  starDisabled: { type: Boolean, default: false },
  isNew: { type: Boolean, default: false },
});
const emit = defineEmits(['toggle-save']);
</script>

<template>
  <div class="lib-row" data-testid="library-row">
    <span class="lib-row__tile" :class="item.mine ? 'lib-row__tile--mine' : 'lib-row__tile--hub'">
      <IconLock v-if="item.mine" color="currentColor" size="23" />
      <IconFile v-else color="currentColor" size="23" />
    </span>
    <span class="lib-row__text">
      <RouterLink :to="`/hub/library/${item.id}`" class="lib-row__name lib-row__link" data-testid="library-row-link">
        {{ item.name }}<span v-if="isNew" class="lib-row__tag lib-row__tag--new" data-testid="new-tag">New</span>
      </RouterLink>
      <span class="lib-row__meta">{{ metaLine(item) }}</span>
    </span>
    <span v-if="item.mine" class="lib-row__tag lib-row__tag--mine">Only you</span>
    <button
      v-if="props.saved !== null"
      type="button"
      class="lib-row__star"
      :class="{ 'is-on': props.saved }"
      :aria-pressed="String(props.saved)"
      :aria-label="`Save ${item.name}`"
      :disabled="starDisabled"
      data-testid="library-star"
      @click="emit('toggle-save', item.id)"
    >
      <IconStar color="currentColor" size="18" />
    </button>
    <IconChevronRight color="currentColor" width="9" height="16" class="lib-row__chev" />
  </div>
</template>

<style scoped>
/* Prototype .row / .ico / .tag (build 30 Sep 12:40). */
.lib-row {
  position: relative; display: flex; align-items: center; gap: 14px; padding: 15px 0;
  border-bottom: 1px solid var(--xl-color-border-subtle);
}
.lib-row:last-child { border-bottom: 0; }
.lib-row__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.lib-row__name { font-size: 15px; font-weight: 700; display: flex; align-items: center; gap: 5px; overflow-wrap: anywhere; }
/* The title is the row's link; its ::after stretches over the row, under the star. */
.lib-row__link { color: inherit; text-decoration: none; }
.lib-row__link::after { content: ''; position: absolute; inset: 0; border-radius: 8px; }
.lib-row__link:focus-visible { outline: none; }
.lib-row__link:focus-visible::after { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; }
.lib-row__meta { font-size: 13px; color: var(--xl-color-outline); }
.lib-row__chev { color: var(--xl-color-outline); flex: 0 0 auto; }

.lib-row__tile { width: 42px; height: 42px; flex: 0 0 42px; border-radius: 13px; display: grid; place-items: center; }
.lib-row__tile--hub { background: var(--xl-color-primary-container); color: var(--xl-color-primary); }
.lib-row__tile--mine { background: var(--xl-color-warning-container); color: var(--xl-color-warning); }

.lib-row__tag {
  display: inline-flex; align-items: center; height: 23px; padding: 0 9px; border-radius: 7px;
  font-size: 11.5px; font-weight: 600; white-space: nowrap; flex: 0 0 auto; line-height: 1;
}
.lib-row__tag--new { background: var(--xl-color-primary-container); color: var(--xl-color-primary); }
.lib-row__tag--mine { background: var(--xl-color-warning-container); color: var(--xl-color-warning); }

/* Prototype `btn g sm` with the star: above the stretched link, so it takes its own tap. */
.lib-row__star {
  position: relative; z-index: 1; flex: 0 0 auto; display: grid; place-items: center;
  width: 34px; height: 34px; border-radius: 99px; border: 1px solid var(--xl-color-border);
  background: var(--xl-color-surface); color: var(--xl-color-outline); cursor: pointer;
}
.lib-row__star:hover:not(:disabled) { color: var(--xl-color-primary); border-color: var(--xl-color-primary-fixed-dim); }
.lib-row__star.is-on { color: var(--xl-color-primary); border-color: var(--xl-color-primary-fixed-dim); background: var(--xl-color-primary-container); }
.lib-row__star.is-on :deep(path) { fill: currentColor; }
.lib-row__star:disabled { opacity: 0.5; cursor: default; }
.lib-row__star:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; }

@media (max-width: 480px) {
  .lib-row__chev { display: none; }
}
</style>
